import z from 'zod';

// Type definition for Firestore Timestamp (environment-agnostic)
export interface FirestoreTimestamp {
  seconds: number;
  nanoseconds: number;
  toDate(): Date;
  toMillis(): number;
}

// Type alias for Timestamp - works with both client and admin SDKs
export type Timestamp = FirestoreTimestamp;

// Represents a Firestore server timestamp sentinel
// This mimics the structure without importing firebase-admin
export interface FieldValueSentinel {
  _sentinel: string;
  _value?: unknown;
}

// Type guard to check if a value is a FieldValue sentinel
export function isFieldValueSentinel(val: unknown): val is FieldValueSentinel {
  return val !== null && typeof val === 'object' && val.constructor.name === 'ServerTimestampTransform';
}

export function hasFieldValueSentinel(val: unknown): boolean {
  return val instanceof Date && sentinels.has(val);
}

const sentinels = new WeakMap<Date, FieldValueSentinel>();

/** ISO or other date strings parseable by Date (e.g. from JSON round-trip). */
export type Dateable = Timestamp | Date | number | string | FieldValueSentinel;

export type Nil = undefined | null;

function isDateable(value: unknown): value is Dateable {
  if (value === undefined || value === null) return false;
  if (isFieldValueSentinel(value)) return true;
  if (value instanceof Date) return true;
  if (typeof value === 'number') return true;
  if (typeof value === 'string') return !Number.isNaN(Date.parse(value));
  if (typeof value === 'object' && 'seconds' in value && 'nanoseconds' in value) return true;
  if (typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') return true;
  return false;
}

function toDate(value: Dateable): Date {
  if (isFieldValueSentinel(value)) {
    const trickyDate = new Date();
    sentinels.set(trickyDate, value);
    return trickyDate;
  }
  if (typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    return value.toDate() as Date;
  }
  if (typeof value === 'string') {
    return new Date(value);
  }
  if (typeof value === 'number') return new Date(value);
  if (value instanceof Date) return value;
  if (typeof value === 'string') return new Date(value);
  throw new TypeError(`Unexpected value: ${typeof value}`);
}

const DateableSchema = z.custom<Dateable>(isDateable);

export function zFirestamp({ defaultToNow = true } = {}) {
  return DateableSchema.nullish().transform((value): Date => {
    if (value === undefined || value === null) {
      if (defaultToNow) return new Date();
      throw new Error(`Value for zFirestamp is required, because defaultToNow is false`);
    }
    return toDate(value);
  });
}

export function zFirestampNow() {
  return zFirestamp({ defaultToNow: true }).default(() => new Date());
}

export function zFirestampOptional() {
  // Legacy Firestore data can contain explicit `null` for optional timestamp fields.
  // Accept Dateable (Date, Timestamp, number, or valid date strings from JSON round-trips).
  // DateableSchema already validates strings via Date.parse, so no separate z.string() needed.
  return DateableSchema.nullish()
    .transform((value): Date | undefined => {
      if (value === undefined || value === null) return undefined;
      return toDate(value);
    })
    .optional();
}

export type ZodFirestampNow = ReturnType<typeof zFirestampNow>;
export type ZodFirestampOpt = ReturnType<typeof zFirestampOptional>;
